import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Eye, Headphones, Layers } from "lucide-react";

import {
    BLS_DOT_COLORS,
    BLS_DOT_SIZES,
    BLS_MODES,
    BLS_PATHS,
    type BlsMode,
    type BlsPath,
    type BlsSettings,
} from "../../../domain/blsChannel";
import { MAX_CYCLES_PER_MINUTE, MIN_CYCLES_PER_MINUTE, clampCyclesPerMinute } from "../../../domain/blsMotion";
import { BLS_DOT_HEX } from "../blsAppearance";

type Props = {
    settings: BlsSettings;
    change: (patch: Partial<BlsSettings>) => void;
    /** Controllers in the patient's hands, or null before they say. */
    patientControllers: number | null;
};

/** Speeds a therapist reaches for, so the slider is for fine-tuning. */
const SPEED_PRESETS = [
    { key: "slow", value: 28 },
    { key: "medium", value: 40 },
    { key: "fast", value: 55 },
] as const;

const MODE_ICON: Record<BlsMode, ReactNode> = {
    visual: <Eye size={16} aria-hidden="true" />,
    audio: <Headphones size={16} aria-hidden="true" />,
    both: <Layers size={16} aria-hidden="true" />,
};

/** The path, drawn: a word for "diagonal ↘" is slower to read than the line. */
const PATH_GLYPH: Record<BlsPath, string> = {
    horizontal: "M3 12 H21",
    diagonalUp: "M4 19 L20 5",
    diagonalDown: "M4 5 L20 19",
    infinity: "M12 12 C9 6 3 6 3 12 C3 18 9 18 12 12 C15 6 21 6 21 12 C21 18 15 18 12 12 Z",
};

const DOT_PX: Record<(typeof BLS_DOT_SIZES)[number], number> = { small: 6, medium: 10, large: 14 };

const chip = (selected: boolean) =>
    `flex min-h-10 items-center justify-center gap-2 rounded-xl border px-3 text-sm font-medium transition-colors ${
        selected
            ? "border-room-accent bg-room-accent-tint text-room-ink"
            : "border-room-line-soft bg-room-active/40 text-room-ink-muted hover:bg-room-hover hover:text-room-ink"
    }`;

function Field({ label, children, aside }: { label: string; children: ReactNode; aside?: ReactNode }) {
    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-room-ink-muted">{label}</span>
                {aside}
            </div>
            {children}
        </div>
    );
}

/**
 * How the stimulus looks and moves. Changed during a set, it reaches the
 * patient at once; before one, it is what the next set starts with.
 */
export default function BoardSettings({ settings, change, patientControllers }: Props) {
    const { t } = useTranslation();

    return (
        <section
            aria-labelledby="emdr-settings-title"
            className="flex flex-col gap-5 rounded-3xl border border-room-line-soft bg-room-raised p-4 sm:p-5"
            data-testid="emdr-settings"
        >
            <h2 id="emdr-settings-title" className="text-base font-semibold text-room-ink">
                {t("emdr.console.settings.title")}
            </h2>

            <Field label={t("emdr.mode.label")}>
                <div role="group" aria-label={t("emdr.mode.label")} className="grid grid-cols-3 gap-1.5">
                    {BLS_MODES.map((mode) => (
                        <button
                            key={mode}
                            type="button"
                            aria-pressed={settings.mode === mode}
                            onClick={() => change({ mode })}
                            className={`${chip(settings.mode === mode)} min-h-14 flex-col gap-1 px-1 text-xs`}
                        >
                            {MODE_ICON[mode]}
                            <span>{t(`emdr.mode.${mode}`)}</span>
                        </button>
                    ))}
                </div>
            </Field>

            <Field
                label={t("emdr.speed.label")}
                aside={
                    <span className="text-sm font-semibold tabular-nums text-room-ink">
                        {t("emdr.speed.value", { count: settings.cyclesPerMinute })}
                    </span>
                }
            >
                <input
                    type="range"
                    min={MIN_CYCLES_PER_MINUTE}
                    max={MAX_CYCLES_PER_MINUTE}
                    step={1}
                    value={settings.cyclesPerMinute}
                    aria-label={t("emdr.speed.label")}
                    onChange={(event) => change({ cyclesPerMinute: clampCyclesPerMinute(Number(event.target.value)) })}
                    className="w-full accent-room-accent"
                />
                <div className="grid grid-cols-3 gap-1.5">
                    {SPEED_PRESETS.map((preset) => (
                        <button
                            key={preset.key}
                            type="button"
                            aria-pressed={settings.cyclesPerMinute === preset.value}
                            onClick={() => change({ cyclesPerMinute: preset.value })}
                            className={chip(settings.cyclesPerMinute === preset.value)}
                        >
                            {t(`emdr.console.settings.speed.${preset.key}`)}
                        </button>
                    ))}
                </div>
            </Field>

            <Field label={t("emdr.path.label")}>
                <div role="group" aria-label={t("emdr.path.label")} className="grid grid-cols-4 gap-1.5">
                    {BLS_PATHS.map((path) => (
                        <button
                            key={path}
                            type="button"
                            aria-pressed={settings.path === path}
                            aria-label={t(`emdr.path.${path}`)}
                            title={t(`emdr.path.${path}`)}
                            onClick={() => change({ path })}
                            className={chip(settings.path === path)}
                        >
                            <svg viewBox="0 0 24 24" className="size-6" aria-hidden="true">
                                <path d={PATH_GLYPH[path]} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
                            </svg>
                        </button>
                    ))}
                </div>
            </Field>

            <div className="grid grid-cols-2 gap-4">
                <Field label={t("emdr.size.label")}>
                    <div role="group" aria-label={t("emdr.size.label")} className="grid grid-cols-3 gap-1.5">
                        {BLS_DOT_SIZES.map((size) => (
                            <button
                                key={size}
                                type="button"
                                aria-pressed={settings.dotSize === size}
                                aria-label={t(`emdr.size.${size}`)}
                                title={t(`emdr.size.${size}`)}
                                onClick={() => change({ dotSize: size })}
                                className={chip(settings.dotSize === size)}
                            >
                                <span
                                    aria-hidden="true"
                                    className="rounded-full bg-current"
                                    style={{ width: DOT_PX[size], height: DOT_PX[size] }}
                                />
                            </button>
                        ))}
                    </div>
                </Field>
                <Field label={t("emdr.color.label")}>
                    <div role="group" aria-label={t("emdr.color.label")} className="grid grid-cols-4 gap-1.5">
                        {BLS_DOT_COLORS.map((color) => (
                            <button
                                key={color}
                                type="button"
                                aria-pressed={settings.dotColor === color}
                                aria-label={t(`emdr.color.${color}`)}
                                title={t(`emdr.color.${color}`)}
                                onClick={() => change({ dotColor: color })}
                                className={`flex min-h-10 items-center justify-center rounded-xl border transition-colors ${
                                    settings.dotColor === color
                                        ? "border-room-accent bg-room-accent-tint"
                                        : "border-room-line-soft bg-room-active/40 hover:bg-room-hover"
                                }`}
                            >
                                {/* Edged, so the white swatch shows on a light console too. */}
                                <span aria-hidden="true" className="size-4 rounded-full border border-room-line" style={{ backgroundColor: BLS_DOT_HEX[color] }} />
                            </button>
                        ))}
                    </div>
                </Field>
            </div>

            <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-room-line-soft bg-room-active/40 p-3">
                <input
                    type="checkbox"
                    role="switch"
                    checked={settings.tactile}
                    onChange={(event) => change({ tactile: event.target.checked })}
                    className="mt-0.5 size-4 accent-room-accent"
                />
                <span className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium text-room-ink">{t("emdr.tactile.label")}</span>
                    <span className="text-xs text-room-ink-muted">{t(controllersCopy(patientControllers))}</span>
                </span>
            </label>
        </section>
    );
}

function controllersCopy(controllers: number | null): string {
    if (controllers === null) return "emdr.tactile.unknown";
    if (controllers === 0) return "emdr.tactile.none";
    return controllers === 1 ? "emdr.tactile.one" : "emdr.tactile.two";
}
